"use client";

import * as React from "react";
import * as ToastPrimitives from "@radix-ui/react-toast";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";

import { cn } from "@/app/lib/utils";

import { TOAST_POSITIONS, ToastPosition, useToast } from "@/app/hooks/use-toast";
import { ToastProvider } from "@radix-ui/react-toast";

const toastVariants = cva(
   "group pointer-events-auto relative flex w-full items-center justify-between space-x-2 overflow-hidden rounded-xl border p-4 pr-6 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full",
   {
      variants: {
         variant: {
            default: "border bg-background text-foreground",
            destructive:
               "destructive group border-destructive bg-destructive text-destructive-foreground",
            success: "success group border-green-500 bg-green-500 text-white",
            info: "info group border-blue-500 bg-blue-500 text-white",
            warning: "warning group border-yellow-500 bg-yellow-500 text-white"
         }
      },
      defaultVariants: {
         variant: "default"
      }
   }
);

const Toast = React.forwardRef<
   React.ElementRef<typeof ToastPrimitives.Root>,
   React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
      VariantProps<typeof toastVariants>
>(({ className, variant, ...props }, ref) => {
   return (
      <ToastPrimitives.Root
         ref={ref}
         className={cn(toastVariants({ variant }), className)}
         {...props}
      />
   );
});
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = React.forwardRef<
   React.ElementRef<typeof ToastPrimitives.Action>,
   React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
   <ToastPrimitives.Action
      ref={ref}
      className={cn(
         "inline-flex h-8 shrink-0 items-center justify-center rounded-xl border bg-transparent px-3 text-sm font-medium transition-colors hover:bg-secondary focus:outline-none focus:ring-1 focus:ring-ring disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive",
         className
      )}
      {...props}
   />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<
   React.ElementRef<typeof ToastPrimitives.Close>,
   React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
   <ToastPrimitives.Close
      ref={ref}
      className={cn(
         "absolute right-1 top-1 rounded-xl p-1 text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-1 group-hover:opacity-100 group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-red-600",
         className
      )}
      toast-close=""
      {...props}
   >
      <X className="h-4 w-4" />
   </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
   React.ElementRef<typeof ToastPrimitives.Title>,
   React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
   <ToastPrimitives.Title
      ref={ref}
      className={cn("text-sm font-semibold [&+div]:text-xs", className)}
      {...props}
   />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
   React.ElementRef<typeof ToastPrimitives.Description>,
   React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
   <ToastPrimitives.Description
      ref={ref}
      className={cn("text-sm opacity-90", className)}
      {...props}
   />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

function Toaster() {
   const { toasts } = useToast();
   const [position, setPosition] = React.useState<ToastPosition>("top-right");

   React.useEffect(() => {
      if (toasts?.[0]?.position) {
         setPosition(toasts[0].position);
      }
   }, [toasts]);

   return (
      <ToastProvider duration={5000}>
         {toasts?.map(({ id, title, description, action, ...props }) => (
            <Toast key={id} {...props}>
               <div className="grid gap-1">
                  {title && <ToastTitle>{title}</ToastTitle>}
                  {description && <ToastDescription>{description}</ToastDescription>}
               </div>
               {action}
               <ToastClose />
            </Toast>
         ))}
         <ToastPrimitives.Viewport
            className={cn(
               "fixed z-[100] flex flex-col gap-2 p-4 w-[390px]",
               position === TOAST_POSITIONS["top-left"] && "top-0 left-0",
               position === TOAST_POSITIONS["top-right"] && "top-0 right-0",
               position === TOAST_POSITIONS["top-center"] &&
                  "top-0 left-1/2 -translate-x-1/2",
               position === TOAST_POSITIONS["bottom-left"] && "bottom-0 left-0",
               position === TOAST_POSITIONS["bottom-right"] && "bottom-0 right-0",
               position === TOAST_POSITIONS["bottom-center"] &&
                  "bottom-0 left-1/2 -translate-x-1/2"
            )}
         />
      </ToastProvider>
   );
}

export {
   Toaster,
   type ToastProps,
   type ToastActionElement,
   ToastProvider,
   Toast,
   ToastTitle,
   ToastDescription,
   ToastClose,
   ToastAction
};
